/**
 * Alpine.js Desktop Photo Viewer Component
 *
 * Immersive lightbox viewer for desktop with:
 * - Keyboard navigation (arrows, Escape)
 * - Click arrows for prev/next
 * - Responsive layout: side-by-side comments (lg+), toggle panel (sm-lg)
 * - SPA navigation with crossfade transitions
 *
 * Composes photoViewerCore for shared state/logic.
 */

import { photoViewerCore } from './photo-viewer-core.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

export function desktopPhotoViewer(config) {
    const core = photoViewerCore(config);

    return {
        ...core,

        // Desktop-only: responsive layout tracking
        isLargeScreen: window.innerWidth >= 1024,

        init() {
            lockBodyScroll();
            this._preloadAdjacentImages(config);
            this._popstateHandler = () => window.location.reload();
            window.addEventListener('popstate', this._popstateHandler);

            this._keyHandler = (e) => this._onKeyDown(e);
            window.addEventListener('keydown', this._keyHandler);

            this._resizeHandler = () => {
                const wasLargeScreen = this.isLargeScreen;
                this.isLargeScreen = window.innerWidth >= 1024;
                if (!wasLargeScreen && this.isLargeScreen && this._commentsStale) {
                    this._fetchComments();
                }
            };
            window.addEventListener('resize', this._resizeHandler);
            this._bindCommentListeners();

            // On large screens, comments sidebar starts visible.
            if (this.isLargeScreen) {
                this.commentsOpen = true;
            }
        },

        destroy() {
            unlockBodyScroll();
            this._releaseCommentListeners();
            window.removeEventListener('popstate', this._popstateHandler);
            window.removeEventListener('keydown', this._keyHandler);
            window.removeEventListener('resize', this._resizeHandler);
        },

        _getCommentsContainer() {
            return this.isLargeScreen
                ? (this.$refs.commentsSidebar || null)
                : (this.$refs.commentsSheetSm || null);
        },

        _refetchCommentsOnNav() {
            if (this.isLargeScreen) {
                this._fetchComments();
            } else {
                this.commentsOpen = false;
            }
        },

        // --- Keyboard Navigation ---

        _onKeyDown(e) {
            if (window._photoModModalOpen || window._reportModalOpen) return;

            // Don't navigate when typing in comment form
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === 'ArrowRight' && this.nextUrl) {
                e.preventDefault();
                this.navigateTo(1);
            }
            if (e.key === 'ArrowLeft' && this.prevUrl) {
                e.preventDefault();
                this.navigateTo(-1);
            }
            if (e.key === 'Escape') {
                if (this.menuOpen) { this.closeMenu(); return; }
                if (this.commentsOpen && !this.isLargeScreen) { this.closeComments(); return; }
                this.goBack();
            }
        },

        // Toggle comments on medium screens; always-on for large
        toggleComments() {
            if (this.isLargeScreen) return;
            if (this.commentsOpen) {
                this.closeComments();
            } else {
                this.openComments();
            }
        }
    };
}

export default desktopPhotoViewer;
